import {
  Banner,
  FlexBox,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { formatE164 } from 'lib-shared-phone-numbers';
import noop from 'lodash/noop';
import { SubmitHandler, useForm } from 'react-hook-form';

import { whatsAppTwilioApi } from 'apis/whatsAppTwilio';
import { HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';

import styles from './whatsAppNumberModal.module.scss';

const formId = 'add-whatsapp-number';

type FieldValues = {
  phoneNumber: string;
  displayName: string;
};

export const AddWhatsAppPhoneNumberModal = createAppModal(
  { type: 'settings:addWhatsAppNumberModal' },
  ({ isOpen, hide, modal }): JSX.Element => {
    const notify = useNotifications();
    const { wabaId } = modal.arg;

    const [addWhatsAppNumber, { isLoading: isAdding }] =
      whatsAppTwilioApi.useAddWhatsAppPhoneNumberMutation();

    const form = useForm<FieldValues>({
      defaultValues: {
        phoneNumber: '',
        displayName: '',
      },
      mode: 'all',
    });

    const onSubmit: SubmitHandler<FieldValues> = async ({
      phoneNumber,
      displayName,
    }) => {
      const formattedE164 = formatE164(phoneNumber);

      if (!formattedE164) {
        form.setError('phoneNumber', {
          type: 'manual',
          message: 'Invalid phone format',
        });

        return;
      }

      try {
        const response = await addWhatsAppNumber({
          wabaId,
          displayName,
          e164: formattedE164,
        }).unwrap();

        if (isResponseWithData(response)) {
          notify(
            {
              title: 'Success',
              text: `"${formattedE164}" has been updated.`,
              icon: 'i-done',
              time: 5000,
            },
            NotificationType.Success,
          );
          hide?.();
        }
      } catch (error) {
        notify(
          {
            title: 'Error while adding phone number.',
            text: 'Something went wrong.',
            icon: 'exclamation',
            className: 'danger',
            time: 5000,
          },
          NotificationType.Error,
        );
      }
    };

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead
          title="Just one last step: Add a phone number"
          onClose={hide}
        />

        <ModalBody>
          <form
            id={formId}
            noValidate
            tabIndex={-1}
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <FormGrid>
              <FormField>
                <HookFormText
                  name="displayName"
                  label="Display Name"
                  control={form.control}
                  rules={{
                    required: 'Display name is required',
                  }}
                />
              </FormField>

              <FormField>
                <HookFormText
                  name="phoneNumber"
                  label="Phone number"
                  control={form.control}
                  rules={{
                    required: 'Phone number is required',
                  }}
                />
              </FormField>
            </FormGrid>

            <FlexBox margin="200 0 0">
              <Banner
                variant="info"
                description={
                  <Text.Paragraph>
                    The display name and phone number should be the same you
                    entered during the Facebook Login flow
                  </Text.Paragraph>
                }
              />
            </FlexBox>
          </form>
        </ModalBody>

        <ModalFoot
          cancelName="Cancel"
          cancelOnClick={hide}
          confirmName="Add phone number"
          confirmOnClick={noop}
          confirmProps={{
            isLoading: isAdding,
            loadingIndicator: 'Adding...',
            type: 'submit',
            form: formId,
            disabled:
              isAdding ||
              !form.formState.isValid ||
              form.formState.isSubmitting ||
              !form.formState.isDirty,
          }}
        />
      </Modal>
    );
  },
);
